/* eslint-disable prefer-promise-reject-errors */
// import { map } from 'lodash';
import { createSlice } from '@reduxjs/toolkit';
// utils
import { Buffer } from 'buffer';
import axios from '../../utils/axios';

import { toUTCISOString } from '../../utils/formatTime';
import { getMimeType } from '../../utils/mediaFormat';

import { resizeImage } from '../../utils/upload';
import { extractThumbnail } from '../../utils/imageExtractFromVideo';
import { getBaseUrl } from '../../utils/urlHelper';

let currentRequestController = null;

const parseParams = (params) => {
  const paramsStringify = JSON.stringify(params);
  return paramsStringify.replace(/"([^"]+)":/g, '$1:').slice(1, -1);
};

const vehicleInfo = `
  vehicleId
  brand
  adTextAfter
  adTextBefore
  advertiserId
  advKms
  body
  builtDate
  cached
  category
  ccRating
  chassisNo
  colour
  compliedDate
  contact
  dealerId
  description
  displayCity
  displayState
  dateCreated
  engineNo
  engineSize
  fuelType
  isDriveAway
  kms
  ladders
  lastUpdate
  locationId
  locationName
  make
  model
  nopics
  photoPath
  photos
  photoChange
  photoChangeBy
  prevPrice
  prevRegistration
  price
  redbookCode
  registrationExpiry
  registrationNo
  registrationState
  seats
  serverLocation
  spareTyres
  status
  stockNo
  subCategory
  supplierStockNo
  virtualSupplierStockNo
  thumbs
  transmission
  type
  variant
  videoPics
  wheels
  year
  minPrice
  videos
  videoThumbs
  videoPath
  manufactureCountry
  glassesCode
  colourInterior
  driveTrain
  doors
  advertisingPlatforms {
    website
    instagram
    facebookMarketPlace
    carsalesComAu
    wholesaleClassifieds
  },
  advertisingHistory {
    carsalesComAu {
      datePublished
      publishedBy
      stockNumber
    },
    facebook {
      datePublished
      publishedBy
      stockNumber
    }
  },
  purchaseDate
  arrivalDate
  acquiredDate
  soldDate
  initialSalePriceEgc
  eventLog {
    oldValue
    newValue
    initiatedBy
    datetime
    field
    event
  }
  updatedBy
  websiteUrl
  videoChange
  videoChangeBy
  addons
  shortDescription
  specialFeatureDescription
  vehicleCategory
`;

const vehicleListInfo = `
  brand
  vehicleId
  chassisNo
  body
  dealerId
  displayCity
  displayState
  engineSize
  fuelType
  kms
  lastUpdate
  locationId
  locationName
  make
  model
  price
  status
  stockNo
  supplierStockNo
  transmission
  type
  variant
  year
  photoPath
  thumbs
  videoThumbs
  registrationNo
  advertisingPlatforms {
    website
    instagram
    facebookMarketPlace
    carsalesComAu
    wholesaleClassifieds
  },
  daysInStock
  completePercentage
  websiteUrl
`;

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  isLoadingReportData: false,
  error: false,
  vehicleErrors: {},
  isUploading: false,
  uploadedAmount: 0, // can be used to track the progress of upload
  // vehicleData: null
  vehicleData: {
    vehicleId: '',
    redbookCode: '',
    chassisNo: '',
    supplierStockNo: '',
    stockNo: '',
    make: '',
    model: '',
    variant: '',
    body: '',
    transmission: '',
    engineNo: '',
    engineSize: '',
    fuelType: '',
    colour: '',
    kms: '',
    advPrice: '',
    minPrice: '',
    year: '',
    locationId: '',
    price: '',
    // kms: undefined,
    // advPrice: undefined,
    // minPrice: undefined,
    // kms: NaN,
    // advPrice: NaN,
    // minPrice: NaN,
    // year: NaN,
    isDriveAway: false,
    registrationState: '',
    description: '',
    adTextAfter: '',
    status: '',
    compliedDate: '',
    builtDate: '',
    seats: '',
    registrationExpiry: '',
    branch: '',
    registrationNo: '',
    // registrationSerial: '',
    formType: '',
    condition: '',
    conditionRating: '',
    keyboardNo: '',
    colourInterior: '',
    doors: 0,
    driveTrain: '',
    engineCapacity: '',
    // serviceHistoryText: '',
    manufactureCountry: '',
    glassesCode: '',
    prevPrice: '',
    prevRegistration: '',
    firstRegistrationYear: '',
    adTextBefore: '',
    advertisingPlatforms: {},
    advertisingHistory: {},
    initialSalePriceEgc: 0,
    websiteUrl: '',
    eventLog: [],
    addons: [],
    locationName: '',
    shortDescription: '',
    specialFeatureDescription: '',
    vehicleCategory: ''
  },
  vehicleList: [],
  vehiclesMovedToNewBrand: [],
  foundTotal: 0,
  vehiclePhotoArchive: ''
};

const slice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.isLoading = true;
    },

    startLoadingReportData(state) {
      state.isLoadingReportData = true;
    },

    // HAS ERROR
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
      console.debug('hasError: ', action.payload);
    },

    // RESET VEHICLE TO INITIAL STATE
    clearVehicleDataSuccess(state) {
      state.vehicleData = initialState.vehicleData;
      state.vehiclePhotoArchive = initialState.vehiclePhotoArchive;
      state.foundTotal = 0;
    },
    // RESET VEHICLE LIST TO INITIAL STATE
    clearVehicleListSuccess(state) {
      state.vehicleList = initialState.vehicleList;
    },

    // GET VEHICLE
    getVehicleSuccess(state, action) {
      state.vehicleData = action.payload;
      state.isLoading = false;
      /* remove getVehicle error */
      delete state.vehicleErrors.getVehicle;
      state.error = false;
    },
    getVehicleFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.getVehicle = action.payload;
    },
    getRecentlyUpdatedVehiclesSuccess(state, action) {
      state.isLoading = false;
      state.recentlyUpdatedVehicles = action.payload.items;
    },
    // GET VEHICLE LIST
    getVehicleListSuccess(state, action) {
      state.isLoading = false;
      // state.vehicleList = action.payload;
      state.vehiclesMovedToNewBrand = initialState.vehiclesMovedToNewBrand;
      state.vehicleList = action.payload.items;
      state.foundTotal = action.payload.count;
      /* remove getVehicleList error */
      delete state.vehicleErrors.getVehicleList;
      state.error = false;
    },
    getVehicleListFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.getVehicleList = action.payload;
    },

    // GET VEHICLE LIST
    getVehicleCountSuccess(state, action) {
      state.isLoading = false;
      state.vehicleCount = action.payload;
      state.error = false;
    },
    // GET VEHICLE AGGREGATIONS
    getVehicleReportDataSuccess(state, action) {
      state.isLoadingReportData = false;
      state.vehicleReportData = action.payload;
      state.error = false;
    },

    // PUT VEHICLE
    putVehicleSuccess(state, action) {
      state.vehicleData = action.payload;
      state.isLoading = false;
      /* remove putVehicle error */
      delete state.vehicleErrors.putVehicle;
      state.error = false;
    },
    putVehicleFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.putVehicle = action.payload;
    },

    // ADVERTISING STATUS
    updateVehicleAdvertisingPlatformsSuccess(state, action) {
      state.isLoading = false;
      state.vehicleList = state.vehicleList.map((item) =>
        item.vehicleId === action.payload.vehicleId
          ? {
              ...item,
              advertisingPlatforms: action.payload.advertisingPlatforms
            }
          : item
      );
    },
    // PAUSE ADVERTISING STATUS
    pauseVehicleAdvertising(state, action) {
      state.isLoading = false;
      state.vehicleList = state.vehicleList.map((item) =>
        item.vehicleId === action.payload.vehicleId
          ? {
              ...item,
              advertisingPlatforms: action.payload.advertisingPlatforms
            }
          : item
      );
    },

    updateVehicleAdvertisingPlatformsFailure(state, action) {
      state.isLoading = false;
      state.error = true;
    },

    updatePauseAdvertisingPlatformsFailure(state, action) {
      state.isLoading = false;
      state.error = true;
    },

    moveVehicleToNewBrandSuccess(state, action) {
      state.isLoading = false;
      state.vehiclesMovedToNewBrand.push(
        action.payload.data.data.moveBranchVehiclesToNewBrand
      );
      state.vehicleList = state.vehicleList.filter(
        (item) => state.vehiclesMovedToNewBrand.indexOf(item.vehicleId) === -1
      );
      console.debug(action.payload);
    },
    moveVehicleToNewBrandFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.moveVehicleToBrand = action.payload;
    },
    // UPLOAD IMAGES
    startImageUpload(state) {
      state.isUploading = true;
      state.uploadedAmount = 0;
    },
    startVideoUpload(state) {
      state.isUploading = true;
      state.uploadedAmount = 0;
    },
    oneImageUploadSuccess(state) {
      state.uploadedAmount += 1;
    },
    oneVideoUploadSuccess(state) {
      state.uploadedAmount += 1;
    },
    uploadImagesSuccess(state) {
      // state.isLoading = false;
      state.isUploading = false;
      /* remove uploadImages error */
      delete state.vehicleErrors.uploadImages;
      state.error = false;
    },
    uploadVideosSuccess(state) {
      // state.isLoading = false;
      state.isUploading = false;
      /* remove uploadImages error */
      delete state.vehicleErrors.uploadImages;
      state.error = false;
    },
    uploadImagesFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.uploadImages = action.payload;
    },
    uploadVideoFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.uploadImages = action.payload;
    },
    // BULK UPLOADS
    startBulkVehicleUpload(state) {
      state.isUploading = true;
      state.bulkVehicleFilesUploaded = 0;
      state.bulkVehicleUploadedTasks = 0;
    },
    bulkUploadVehicleSuccess(state, action) {
      state.bulkVehicleFilesUploaded += 1;
      state.bulkVehicleUploadedTasks += action.payload.numberOfTasksCreated;
    },
    bulkUploadVehiclesSuccess(state) {
      state.isUploading = false;
      delete state.vehicleErrors.bulkUpload;
      state.error = false;
    },
    uploadBulkVehicleFailure(state, action) {
      state.isLoading = false;
      state.isUploading = false;
      state.error = true;
      state.vehicleErrors.bulkUpload = action.payload;
    },

    // GET OLD PICS
    getVehiclePhotoArchiveSuccess(state, action) {
      state.vehiclePhotoArchive = action.payload;
      state.isLoading = false;
      /* remove scanForVehiclePics error */
      delete state.vehicleErrors.vehiclePhotoArchive;
      state.error = false;
    },
    getVehiclePhotoArchiveFailure(state, action) {
      state.isLoading = false;
      state.error = true;
      state.vehicleErrors.vehiclePhotoArchive = action.payload;
    },
    deleteVehicleSuccess(state, action) {
      state.isLoading = false;
      state.error = false;

      state.vehicleList = state.vehicleList.filter(
        (vehicle) => vehicle.vehicleId !== action.payload
      );
    },
    deleteVehicleFailure(state, action) {
      state.error = true;
      state.vehicleErrors.deleteVehicle = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// // Actions
// export const { onToggleFollow } = slice.actions;

//
// ------------------- Pre-processing functions ---------------------------------------------------
//

const updateAdvertisingBasedOnStatus = (vehicleData) => {
  const newVehData = vehicleData;
  const { status } = vehicleData;
  if (status && status === 'Out Of Stock')
    newVehData.advertisingPlatforms = {
      website: false,
      carsalesComAu: false,
      facebookMarketPlace: false,
      instagram: false,
      wholesaleClassifieds: false
    };

  return newVehData;
};

const filterVehicleAttributes = (vehicleData) => {
  const vehInfoStr = vehicleInfo.replace(/\n\s+|\n/g, ' '); // replace new line with following spaces or single new line with single space
  const vehInfoParts = vehInfoStr.split(' '); // break string by spaces
  // first and last element of array might be '' and we don't want them
  // remove eventLog
  const accceptableAtributes = vehInfoParts.filter(
    (element) => element !== '' && element !== 'eventLog'
  );

  const vehicleFiltered = {};
  const keys = Object.keys(vehicleData);

  keys.forEach((key) => {
    console.log('key = ', key);
    if (vehicleData[key] && accceptableAtributes.includes(key))
      vehicleFiltered[key] = vehicleData[key];
  });

  // return vehicleFiltered;
  return updateAdvertisingBasedOnStatus(vehicleFiltered);
};

/* VehicleForm.js throws a warning if value == null
 * Therefore, here all null attributes replaced with empty strings from initialState */
const replaceNullWithEmptyString = (vehicleData) => {
  const newVehicleData = {
    ...initialState.vehicleData
  };
  const keys = Object.keys(vehicleData);
  keys.forEach((key) => {
    if (vehicleData[key]) newVehicleData[key] = vehicleData[key];
  });

  return newVehicleData;
};

// function replaceNumbersWithStrings(vehicleData) {
//   const newData = vehicleData;
//   if (newData.kms) newData.kms = newData.kms.toString();

//   return newData;
// }

const replaceStringsWithNumbers = (vehicleData) => {
  const newData = { ...vehicleData };
  if (newData.year) newData.year = Number(newData.year);
  if (newData.kms) newData.kms = Number(newData.kms);
  if (newData.price) newData.price = Number(newData.price);
  if (newData.minPrice) newData.minPrice = Number(newData.minPrice);
  if (newData.doors) newData.doors = Number(newData.doors);
  if (newData.seats) newData.seats = Number(newData.seats);

  return newData;
};

// ----------------------------------------------------------------------

const makeValidQueryInput = (searchParams) => {
  /* get valid search params or simply get rid of empty attributes */

  let inputString = '';
  const keys = Object.keys(searchParams);

  keys.forEach((key) => {
    /* skip on empty value */
    if (!searchParams[key]) return;

    // Present in Form but not used yet
    //       sortBy: enum
    //       isDriveAway: boolean
    //       status: ""
    //       variant: ""

    if (key === 'keywords') {
      // const keywords = searchParams[key].replace(/["'‘’`]/g, '"'); // replace any quotes with double quotes
      const keywords = searchParams[key];
      const updatedKeywords = JSON.stringify(keywords); // replace " with \"
      console.debug('updatedKeywords = ', updatedKeywords);
      inputString += ` ${key}: ${updatedKeywords}`;
    }

    if (
      key === 'status' ||
      key === 'stockNo' ||
      key === 'supplierStockNo' ||
      key === 'chassisNo' ||
      key === 'redbookCode' ||
      key === 'yearMin' ||
      key === 'yearMax' ||
      key === 'priceMin' ||
      key === 'priceMax' ||
      key === 'kmsMin' ||
      key === 'kmsMax' ||
      key === 'soldDate' ||
      key === 'locationName'
    )
      inputString += ` ${key}: "${searchParams[key]}"`;

    /* Next values are not strings, therefore don't require to be in quotes    ".." */
    if (key === 'itemsAmount' || key === 'itemsOffset' || key === 'sortBy') {
      inputString += ` ${key}: ${searchParams[key]}`;
    }

    if (key === 'special') {
      inputString += ` ${key}: { ${searchParams[key][0]} : true } `;
    }

    /* Next values expected to be arrays */
    if (
      key === 'brandList' ||
      key === 'locationId' ||
      key === 'make' ||
      key === 'model' ||
      key === 'body' ||
      key === 'displayState'
    ) {
      /* input value should be an array */
      if (
        typeof searchParams[key] === 'string' ||
        typeof searchParams[key] === 'number'
      ) {
        const array = [searchParams[key]];
        inputString += ` ${key}: ${JSON.stringify(array)}`;
      } else if (Array.isArray(searchParams[key])) {
        inputString += ` ${key}: ${JSON.stringify(searchParams[key])}`;
      }
    }

    if (key === 'sortParams') {
      const descending = searchParams[key].descending ? 'descending: true' : '';
      inputString += ` ${key}: { attribute: "${searchParams[key].attribute}" ${descending}}`;
    }

    if (key === 'advertisingPlatforms') {
      inputString += ` ${key}: { website : ${searchParams[key].includes(
        'Website'
      )} carsalesComAu : ${searchParams[key].includes(
        'Carsales.com.au'
      )} instagram : ${searchParams[key].includes(
        'Instagram'
      )} facebookMarketPlace : ${searchParams[key].includes(
        'Facebook'
      )} wholesaleClassifieds : ${searchParams[key].includes(
        'wholesaleClassifieds'
      )}} 
      `;
    }
  });

  return inputString;
};

const updateVideoArray = (vehicleData, newVideos) => {
  // Initialize photoPath, photos, and thumbs with existing data or empty arrays
  const videoPath =
    newVideos.length > 0
      ? `${newVideos[0].baseUrl}/`
      : vehicleData.videoPath || '';

  const videos = vehicleData.videos ? [...vehicleData.videos] : [];
  const videoThumbs = vehicleData.videoThumbs
    ? [...vehicleData.videoThumbs]
    : [];

  // Loop through new video and update the videos and videoThumbs arrays
  newVideos.forEach((video) => {
    // Extract the path parts directly from the URLs
    const videoFile = video.videoUrl;
    const videoThumbFile = video.videoThumbUrl;

    if (videoFile) {
      videos.push(videoFile);
    }

    if (videoThumbFile) {
      videoThumbs.push(videoThumbFile);
    }
  });

  return {
    ...vehicleData,
    videoPath,
    videos,
    videoThumbs
  };
};

// ----------------------------------------------------------------------

export function clearVehicleData() {
  // console.debug('clearVehicleData()');
  return async (dispatch) => {
    dispatch(slice.actions.clearVehicleDataSuccess());
  };
}

export function clearVehicleList() {
  // console.debug('clearVehicleList()');
  return async (dispatch) => {
    dispatch(slice.actions.clearVehicleListSuccess());
  };
}

export function getVehicleCount(brand, searchParams = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const searchParamsInput = makeValidQueryInput(searchParams);
    console.debug(searchParams);
    const queryString = `query {
      getAnalytics(
        searchParams: { ${searchParamsInput} },
        brand: "${brand}"
      ) {
        noPhotos,
        lessThan5Photos,
        inStock90Days,
        inStock7Days,
        numberOfPublishedItems,
        numberOfItemsInStock,
        itemsWithoutDescriptions,
        noPrice, 
        total
      }
    }`;

    try {
      const response = await axios.post('/graphql', { query: queryString });

      dispatch(
        slice.actions.getVehicleCountSuccess(response.data.data.getAnalytics)
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

const getTimeFrameInMinutes = (timeFrame) => {
  switch (timeFrame) {
    case 'Day':
      return 1;
    case 'Week':
      return 7;
    case 'Month':
      return 30;
    case 'Quarter':
      return 90;
    case 'Year':
      return 360;
    case 'All':
      return 0;
    default:
      return 0;
  }
};

export function getVehicleReportData(
  brand,
  frequency,
  timeFrame,
  searchParams = {}
) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoadingReportData());

    const timeFrameInMinutes = getTimeFrameInMinutes(timeFrame);

    if (timeFrameInMinutes > 0) {
      const today = new Date();
      const adjustedDate = new Date(today);
      adjustedDate.setDate(today.getDate() - timeFrameInMinutes);
      const formattedDate = toUTCISOString(adjustedDate);
      searchParams.soldDate = formattedDate;
    } else {
      searchParams.soldDate = '1970-01-01T00:00:00.000Z'; // Set to minimum unix date
    }

    console.debug(
      `Sold Date: ${searchParams.soldDate} = ${timeFrameInMinutes}`
    );
    const searchParamsInput = makeValidQueryInput(searchParams);
    console.debug(searchParams);
    const queryString = `query {
      getVehicleReportData(
        searchParams: { ${searchParamsInput} },
        brand: "${brand}",
        frequency: "${frequency}"
      ) {
        soldDates { key, value },
        makesAndModels {
          make
          value
          models {
              model
              value
          }
        },
        statusCodes  { key, value }
        avgLeadTimeOverTime  { key, value }
        avgLeadTime
        totalNumberOfSales
        totalAmount
        avgPriceChangePerMake { key, value }
      }
    }`;

    try {
      const response = await axios.post('/graphql', { query: queryString });

      dispatch(
        slice.actions.getVehicleReportDataSuccess(
          response.data.data.getVehicleReportData
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function getRecentlyUpdatedVehicles(brand) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    const queryString = `query findVehicles {
    findVehiclesAuth(brand: "${brand}", searchParams: { 
      itemsOffset: 0, 
      itemsAmount: 10,
      sortParams: { attribute: "lastUpdate", descending: true }}) { 
      count 
      items {
        vehicleId
        supplierStockNo
        make 
        model
        variant
        lastUpdate
        updatedBy
        status
        daysInStock
        completePercentage
        locationName
      } 
    } }`;

    const response = await axios.post('/graphql', { query: queryString });

    try {
      dispatch(
        slice.actions.getRecentlyUpdatedVehiclesSuccess(
          response.data.data.findVehiclesAuth
        )
      );
    } catch (error) {
      dispatch(slice.actions.hasError(error));
      console.error('Failed to obtain latest vehicle updates');
    }
  };
}

export function getVehicleList(brand, searchParams = {}) {
  console.debug('getVehicleList()');
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    // const brand = 'DUTTONONE';
    // let branchList = '';

    /* check if locationIDs from searchParams are all accessible by current user */

    const searchParamsInput = makeValidQueryInput(searchParams);

    const queryString = `query {
      findVehiclesAuth(
        brand: "${brand}",
        searchParams: { ${searchParamsInput} }
      ) {
        count,
        items {
          ${vehicleListInfo}
        }
      }
    }`;
    // console.debug('queryString = ', queryString);

    // const validSearchParams = getValidSearchParams(searchParams);
    // console.debug('validSearchParams = ', validSearchParams);

    try {
      const response = await axios.post('/graphql', { query: queryString });
      // const response = await axios.post('/graphql', {
      //   query: FIND_VEHICLES_QUERY,
      //   variables: {
      //     brand,
      //     searchParams: { ...validSearchParams }
      //   }
      // });
      dispatch(
        slice.actions.getVehicleListSuccess(
          // response.data.data.findVehicles.items
          response.data.data.findVehicles
        )
      );
    } catch (error) {
      dispatch(slice.actions.getVehicleListFailure('Failed to fetch vehicles'));
      dispatch(slice.actions.clearVehicleListSuccess());
    }
  };
}

export function findVehiclesAuth({ brand, searchParams }) {
  console.debug('findVehiclesAuth()', { brand, searchParams });
  const sParams = searchParams || {};
  return async (dispatch) => {
    // If there is an ongoing request, cancel it before starting a new one
    if (currentRequestController) {
      currentRequestController.abort(); // Cancel the previous request
    }

    // Create a new AbortController for the new request
    currentRequestController = new AbortController();
    const { signal } = currentRequestController;

    dispatch(slice.actions.startLoading());

    // Prepare searchParams for the query
    const searchParamsInput = makeValidQueryInput(sParams);
    // console.debug('sParams = ', sParams);

    const queryString = `query {
      findVehiclesAuth(
        ${brand && !sParams.brandList ? `brand: "${brand}"` : ''}
        searchParams: { ${searchParamsInput} }
      ) {
        count,
        items {
          ${vehicleListInfo}
        }
      }
    }`;
    // console.debug('queryString = ', queryString);

    try {
      const response = await axios.post(
        '/graphql',
        { query: queryString },
        { signal }
      );

      dispatch(
        slice.actions.getVehicleListSuccess(
          // response.data.data.findVehicles.items
          response.data.data.findVehiclesAuth
        )
      );
    } catch (error) {
      // If the request was canceled, don't handle it as a failure
      if (error.name === 'CanceledError') {
        console.log('Request was canceled:', error.message);
      } else {
        console.error('Error fetching vehicles:', error);

        // Handle error by dispatching failure actions
        dispatch(
          slice.actions.getVehicleListFailure('Failed to fetch vehicles')
        );
        dispatch(slice.actions.clearVehicleListSuccess());
      }
    } finally {
      // Reset the currentRequestController after completion
      currentRequestController = null;
    }
  };
}

export function getVehicle(vehicleId, brand) {
  console.debug('getVehicle()');
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query getVehicle {
        getVehicle(vehicleId: "${vehicleId}", brand: "${brand}") { ${vehicleInfo} }
      }`;
      // console.debug('queryString = ', queryString);
      const response = await axios.post('/graphql', { query: queryString });
      console.debug('response = ', response);

      /* Empty values for attributes that returned as null from the backend are added to fix Warning.
       * Warning: `value` prop on `input` should not be null. Consider using an empty string to clear the component or `undefined` for uncontrolled components. */
      const getVehicleData =
        response && response.data && response.data.data.getVehicle
          ? response.data.data.getVehicle
          : undefined;
      const newVehicle = {
        ...initialState.vehicleData
      };
      Object.keys(getVehicleData).forEach((key) => {
        if (getVehicleData[key] !== null) newVehicle[key] = getVehicleData[key];
      });
      console.debug('newVehicle = ', newVehicle);
      dispatch(slice.actions.getVehicleSuccess(newVehicle));
    } catch (error) {
      dispatch(slice.actions.getVehicleFailure('Failed to fetch vehicle'));
      dispatch(slice.actions.clearVehicleDataSuccess());
    }
  };
}

// ----------------------------------------------------------------------

const putVehicleOperation = async (brand, vehicleData) => {
  console.log(vehicleData, 'vehicleData>>>>>');
  /* if status === Sold then turn off advertisement */
  const newVehData =
    vehicleData.status && vehicleData.status === 'Sold'
      ? {
          ...vehicleData,
          advertisingPlatforms: {
            website: false,
            instagram: false,
            facebookMarketPlace: false,
            carsalesComAu: false,
            wholesaleClassifieds: false
          }
        }
      : { ...vehicleData };

  /* Not all attributes of the Vehicle Form are acceptable by the backend api, so here we filter out what's backend doesn'r have yet */
  const vehicleFiltered = filterVehicleAttributes(newVehData);
  console.debug('vehicleFiltered = ', vehicleFiltered);
  const queryString = `mutation { putVehicle(brand: "${brand}", vehicle: {${parseParams(
    // vehicleData
    vehicleFiltered
  )}}) { ${vehicleInfo} }
  }`;
  const response = await axios.post('/graphql', { query: queryString });
  /* if mutation returned errors, then throw an error */
  if (response.data.errors && response.data.errors.length > 0) {
    throw new Error(response.data.errors[0].message);
    // throw new Error(response.data.errors);
  }

  /* if no errors, then proceed */
  const vehData =
    response && response.data && response.data.data.putVehicle
      ? response.data.data.putVehicle
      : null;
  return vehData;
};

/* This is used for the PDF Forms to update the Initial Sale 
price without updating the page */
export async function putVehicleAsync(brand, vehicleInfo) {
  const vehicleData = replaceStringsWithNumbers(vehicleInfo);
  const vehData = await putVehicleOperation(brand, vehicleData);
  return vehData;
}

export function putVehicle(brand, vehicleInfo) {
  const vehicleData = replaceStringsWithNumbers(vehicleInfo);
  console.debug('putVehicle()', vehicleData);
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const vehData = await putVehicleOperation(brand, vehicleData);
      /* add empty strings for null attributes (VehicleForm.js needs it) */
      const newVehicleData = replaceNullWithEmptyString(vehData);
      dispatch(slice.actions.putVehicleSuccess(newVehicleData));
    } catch (error) {
      dispatch(slice.actions.putVehicleFailure(error.message));
    }
  };
}

export function deleteVehicle(vehicleId, brand, chassisNo) {
  console.debug('deleteVehicle()', vehicleId);
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { deleteVehicle(vehicleId: "${vehicleId}", brand: "${brand}", chassisNo: "${chassisNo}") }`;
      const response = await axios.post('/graphql', { query: queryString });
      /* if mutation returned errors, then throw an error */
      if (response.data.errors && response.data.errors.length > 0) {
        throw new Error(response.data.errors[0].message);
      }
      dispatch(slice.actions.deleteVehicleSuccess(vehicleId));
    } catch (error) {
      dispatch(slice.actions.deleteVehicleFailure(error.message));
    }
  };
}

export function updateVehicleAdvertisingPlatforms(
  brand,
  branchId,
  vehicleId,
  advertisingPlatforms
) {
  console.debug('updateVehicleAdvertisingPlatforms()', advertisingPlatforms);
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    const updatedAdvertisingPlatforms = JSON.stringify(
      advertisingPlatforms
    ).replace(/"/g, '');

    try {
      const queryString = `mutation { 
    updateVehicleAdvertisingPlatforms(vehicleId:"${vehicleId}", brand: "${brand}", branchId:"${branchId}", advertisingPlatforms: ${updatedAdvertisingPlatforms}) 
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(
        slice.actions.updateVehicleAdvertisingPlatformsSuccess({
          vehicleId,
          advertisingPlatforms
        })
      );
    } catch (error) {
      dispatch(
        slice.actions.updateVehicleAdvertisingPlatformsFailure(error.message)
      );
    }
  };
}

export function updatePauseVehicleAdvertisingPlatforms(
  vehicleId,
  brand,
  branchId,
  companyName
) {
  console.log(
    vehicleId,
    brand,
    branchId,
    companyName,
    'vehicleId,brand,branchId'
  );
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const queryString = `mutation { 
    pauseAdvertisingPlatforms(vehicleId:"${vehicleId}", brand: "${brand}", branchId:"${branchId}", companyName:"${companyName}") 
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(
        slice.actions.pauseVehicleAdvertising({
          vehicleId
        })
      );
    } catch (error) {
      dispatch(
        slice.actions.updatePauseAdvertisingPlatformsFailure(error.message)
      );
    }
  };
}

export function moveVehicleToNewBrand(
  branchId,
  currentBrand,
  newBrand,
  vehicleId
) {
  console.debug(`moveVehicleToNewBrand(${vehicleId})`);
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `mutation { 
        moveBranchVehiclesToNewBrand(id: "${branchId}",  currentBrand: "${currentBrand}", newBrand: "${newBrand}", vehicleId:"${vehicleId}")
      }`;

      const response = await axios.post('/graphql', { query: queryString });

      if (response.data.errors && response.data.errors.length > 0)
        throw new Error(response.data.errors[0].message);

      dispatch(slice.actions.moveVehicleToNewBrandSuccess(response));
    } catch (error) {
      dispatch(slice.actions.moveVehicleToNewBrandFailure(error.message));
    }
  };
}

// ----------------------------------------------------------------------
function readFileAsync(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

const updatePhotosThumbs = (vehicleData, newImages) => {
  // Initialize photoPath, photos, and thumbs with existing data or empty arrays
  const photoPath =
    newImages.length > 0
      ? `${newImages[0].baseUrl}/`
      : vehicleData.photoPath || '';

  const photos = vehicleData.photos ? [...vehicleData.photos] : [];
  const thumbs = vehicleData.thumbs ? [...vehicleData.thumbs] : [];

  // Loop through new images and update the photos and thumbs arrays
  newImages.forEach((image) => {
    if (image.imageSize === 'small') {
      const photoFile = image.photoUrl.split('amazonaws.com/')[1];
      const thumbFile = image.thumbUrl.split('amazonaws.com/')[1];

      photos.push(photoFile);
      thumbs.push(thumbFile);
    } else {
      // Extract the path parts directly from the URLs
      const photoFile = image.photoUrl;
      const thumbFile = image.thumbUrl;

      if (photoFile) {
        photos.push(photoFile);
      }

      if (thumbFile) {
        thumbs.push(thumbFile);
      }
    }
  });

  return {
    ...vehicleData,
    photoPath,
    photos,
    thumbs
  };
};

const updateVideos = (vehicleData, newVideos) => {
  let videoPath = vehicleData.videoPath ? vehicleData.videoPath : '';
  const videos = [];
  const videoThumbs = [];

  if (vehicleData.videos) {
    for (let i = 0; i < vehicleData.videos.length; i += 1) {
      videos.push(vehicleData.videos[i]);
      videoThumbs.push(vehicleData.videoThumbs[i]);
    }
  }

  for (let i = 0; i < newVideos.length; i += 1) {
    const imagePhotoPath = newVideos[i].videoUrl.split('brands/')[0];
    const videoFile = newVideos[i].videoUrl.split('amazonaws.com/')[1];
    const thumbFile = newVideos[i].videoThumbUrl.split('amazonaws.com/')[1];

    videoPath = imagePhotoPath;
    videos.push(videoFile);
    videoThumbs.push(thumbFile);
  }

  return {
    ...vehicleData,
    videoPath,
    videos,
    videoThumbs
  };
};

export function bulkUploadVehicles(files, source) {
  console.debug('bulkUploadVehicles()');
  // const { chassisNo, vehicleId } = vehicleData;
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startBulkVehicleUpload());
    if (!source) {
      dispatch(slice.actions.uploadBulkVehicleFailure('No source selected'));
    }
    try {
      await Promise.all(
        files.map(async (file) => {
          const contentBuffer = await readFileAsync(file);
          const data = Buffer.from(contentBuffer, 'string');
          const vehicles = JSON.parse(data);
          const queryString = `mutation { 
            bulkUploadVehicles(
              data: ${JSON.stringify(
                JSON.stringify(vehicles)
              )}, source: ${JSON.stringify(source)}
            ) { numberOfTasksCreated }
          }`;
          const response = await axios.post('/graphql', { query: queryString });

          if (response.data.errors && response.data.errors.length > 0)
            throw new Error(response.data.errors[0].message);

          /* successful operation: */
          const { numberOfTasksCreated } =
            response.data.data.bulkUploadVehicles;

          dispatch(
            slice.actions.bulkUploadVehicleSuccess({ numberOfTasksCreated })
          );
        })
      );
      console.debug(`Successfully uploaded ${files.length} files`);
      dispatch(slice.actions.bulkUploadVehiclesSuccess());
    } catch (error) {
      console.error('Failed to upload data file(s).', error);
      dispatch(
        slice.actions.uploadBulkVehicleFailure('Failed to upload data file(s)')
      );
    }
  };
}

async function uploadFileToS3(url, file) {
  try {
    const response = await axios.put(url, file, {
      headers: {
        'Content-Type': file.type,
        // Explicitly clear any Authorization header
        Authorization: undefined
      }
    });
    console.log('File uploaded successfully:', response.status);
  } catch (error) {
    console.error('Error uploading file:', error);
    console.error('Error details:', error.response?.data || error.message);
  }
}

export function uploadImages(brand, vehicleData, files) {
  console.debug('uploadImages()');
  const IMAGE_SIZE_LIMIT = 10 * 1024 * 1024; // 10 MB
  const { chassisNo, vehicleId } = vehicleData;

  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startImageUpload());

    const uploadedPhotosThumbs = [];

    try {
      await Promise.all(
        files.map(async (file) => {
          if (file.size < IMAGE_SIZE_LIMIT) {
            try {
              const contentBuffer = await readFileAsync(file);

              // Request presigned URLs via GraphQL mutation
              const queryString = `
                mutation {
                  uploadLargeImage(
                    brand: "${brand}", 
                    chassisNo: "${chassisNo}", 
                    vehicleId: "${vehicleId}"
                  ) {
                    photoPresignedUrl
                    thumbPresignedUrl
                    photoPath
                    thumbPath
                  }
                }
              `;

              const response = await axios.post('/graphql', {
                query: queryString
              });

              if (response.data.errors?.length > 0) {
                throw new Error(response.data.errors[0].message);
              }

              const {
                photoPresignedUrl,
                thumbPresignedUrl,
                photoPath,
                thumbPath
              } = response.data.data.uploadLargeImage;

              // Create a thumbnail blob
              const thumbBlob = await resizeImage(file, 600);

              // Upload to S3 using presigned URLs
              await uploadFileToS3(photoPresignedUrl, file);
              await uploadFileToS3(thumbPresignedUrl, thumbBlob);

              const baseUrl = getBaseUrl(photoPresignedUrl);
              uploadedPhotosThumbs.push({
                imageSize: 'large',
                baseUrl,
                localPath: file.path,
                photoUrl: photoPath,
                thumbUrl: thumbPath
              });

              dispatch(slice.actions.oneImageUploadSuccess());
            } catch (error) {
              console.error(`Failed to upload file: ${file.path}`, error);
              dispatch(
                slice.actions.uploadImagesFailure(
                  `Failed to upload file: ${file.path}`
                )
              );
            }
          }
        })
      );

      console.debug(
        `Successfully uploaded ${uploadedPhotosThumbs.length} of ${files.length} files`
      );

      // Ensure uploaded URLs match the order of input files
      const picUrlsToSave = files
        .map((file) =>
          uploadedPhotosThumbs.find((item) => item.localPath === file.path)
        )
        .filter((item) => item);

      // Update photos and thumbs in vehicleData
      const newVehicleData = updatePhotosThumbs(vehicleData, picUrlsToSave);
      console.log('newVehicleData = ', newVehicleData);

      // Update DynamoDB table
      try {
        const vehData = await putVehicleOperation(brand, newVehicleData);
        dispatch(slice.actions.putVehicleSuccess(vehData));
        dispatch(slice.actions.uploadImagesSuccess());
      } catch (error) {
        console.error('Failed to update vehicle data.', error);
        dispatch(slice.actions.putVehicleFailure('Failed to save vehicle'));
      }
    } catch (error) {
      console.error('Failed to upload images.', error);
      dispatch(slice.actions.uploadImagesFailure('Failed to upload images'));
    }
  };
}

export function uploadVideos(brand, vehicleData, files) {
  console.debug('uploadVideos()');
  const VIDEO_SIZE_LIMIT = 24000000; // 24 MB
  const { chassisNo, vehicleId } = vehicleData;

  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    dispatch(slice.actions.startVideoUpload());

    const uploadedVideoThumbs = [];

    try {
      await Promise.all(
        files.map(async (file) => {
          if (file.size < VIDEO_SIZE_LIMIT) {
            try {
              const contentBuffer = await readFileAsync(file);

              // Construct GraphQL mutation to get pre-signed URLs
              const queryString = `
                mutation { 
                  uploadVideo(
                    brand: "${brand}", 
                    chassisNo: "${chassisNo}", 
                    vehicleId: "${vehicleId}",
                    videoExtension: "${file.name.split('.').pop()}"
                  ) { 
                    videoPresignedUrl
                    videoThumbPresignedUrl
                    videoPath
                    videoThumbPath 
                  }
                }
              `;
              const response = await axios.post('/graphql', {
                query: queryString
              });

              if (response.data.errors && response.data.errors.length > 0) {
                throw new Error(response.data.errors[0].message);
              }

              const {
                videoPresignedUrl,
                videoThumbPresignedUrl,
                videoPath,
                videoThumbPath
              } = response.data.data.uploadVideo;

              // Extract thumbnail from the video
              const thumbBlob = await extractThumbnail(file);
              const resizedThumbBlob = await resizeImage(thumbBlob, 600);

              // Upload video and thumbnail to S3
              await uploadFileToS3(videoPresignedUrl, file);
              await uploadFileToS3(videoThumbPresignedUrl, resizedThumbBlob);

              const baseUrl = getBaseUrl(videoPresignedUrl);
              uploadedVideoThumbs.push({
                imageSize: 'large',
                baseUrl,
                localPath: file.path,
                videoUrl: videoPath,
                videoThumbUrl: videoThumbPath
              });

              dispatch(slice.actions.oneImageUploadSuccess());
            } catch (error) {
              console.error(`Failed to upload file: ${file.path}`, error);
              dispatch(
                slice.actions.uploadImagesFailure(
                  `Failed to upload file: ${file.path}`
                )
              );
            }
          }
        })
      );

      console.debug(
        `Successfully uploaded ${uploadedVideoThumbs.length} of ${files.length} files`
      );

      // Ensure uploaded URLs match the order of input files
      const picUrlsToSave = files
        .map((file) =>
          uploadedVideoThumbs.find((item) => item.localPath === file.path)
        )
        .filter((item) => item);

      console.log('picUrlsToSave = ', picUrlsToSave);

      // Update photos and thumbs in vehicleData
      const newVehicleData = updateVideoArray(vehicleData, picUrlsToSave);
      console.log('newVehicleData = ', newVehicleData);

      // Update DynamoDB table
      try {
        const vehData = await putVehicleOperation(brand, newVehicleData);
        dispatch(slice.actions.putVehicleSuccess(vehData));
        dispatch(slice.actions.uploadVideosSuccess());
      } catch (error) {
        console.error('Failed to update vehicle data.', error);
        dispatch(slice.actions.putVehicleFailure('Failed to save vehicle'));
      }
    } catch (error) {
      console.error('Failed to upload images.', error);
      dispatch(slice.actions.uploadVideoFailure('Failed to upload images'));
    }
  };
}

export function getVehiclePhotoArchive(brand, chassisNo) {
  console.debug('getVehiclePhotoArchive: ', { brand, chassisNo });
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const queryString = `query { 
        scanForVehiclePics(brand: "${brand}", chassisNo: "${chassisNo}") {
          brand
          vehicleId
          chassisNo
          photoPath
          photos
          thumbs
        }
      }`;
      console.debug('queryString = ', queryString);
      const response = await axios.post('/graphql', { query: queryString });
      const vehiclePics = response.data.data.scanForVehiclePics;
      console.debug('scanForVehiclePics vehiclePics = ', vehiclePics);
      dispatch(slice.actions.getVehiclePhotoArchiveSuccess(vehiclePics));
    } catch (error) {
      dispatch(slice.actions.getVehiclePhotoArchiveFailure(error.message));
    }
  };
}

// // ----------------------------------------------------------------------

// export function clearVehicle() {
//   console.debug('clearVehicle()');
// }
